import { useState, type ChangeEvent } from "react"
import Input from "@/v2-ui/input"
import { errorToast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import TranslationAddObjectsPreviewRow from "@/v2-backoffice-client/translation/addObjects/TranslationAddObjectsPreviewRow"
import TranslationAddObjectsStep from "@/v2-backoffice-client/translation/addObjects/TranslationAddObjectsStep"
import { useTranslationGenerateTypeValuesMutation } from "@/v2-backoffice-client/translation/addObjects/translation.generateTypeValues.mutation"
import { useTranslationSeedTypeValuesMutation } from "@/v2-backoffice-client/translation/addObjects/translation.seedTypeValues.mutation"
import { ADD_OBJECTS_SOURCE_LANGUAGES } from "@/v2-backoffice-client/translation/translation.constants"
import {
  findTableIssues,
  parseTable,
  type Generated,
  type ParsedTable,
  type SeedResult,
} from "@/v2-backoffice-client/translation/addObjects/translation.addObjects.utils"

const EXAMPLE_ROWS = [
  "Code;Name;Description",
  "1;Eier;Tinglyst eier av eiendommen",
  "2;Fester;Har festerett til eiendommen",
  "3;Framfester;Fester som har festet bort videre",
].join("\n")

export type TranslationAddObjectsViewProps = {
  // Called after every value seeded successfully, with the saved type set
  // (= its catalog category key) — the board navigates there so the
  // operator can verify the new rows without searching for them.
  onSeeded(typeSet: string): void
}

// Add type values: a guided flow that seeds labels for DATA values (rows of
// common.TypeValues + their common.Translations) as opposed to the UI texts
// (common.Features) the rest of the tab manages. Paste/upload a table of
// values, verify the parse, AI-translate every supported language, review,
// then seed to the database.
function TranslationAddObjectsView(props: TranslationAddObjectsViewProps) {
  const { onSeeded } = props
  const [typeSet, setTypeSet] = useState("")
  const [rawText, setRawText] = useState("")
  const [fileName, setFileName] = useState<string | null>(null)
  const [hasHeader, setHasHeader] = useState(true)
  const [parsed, setParsed] = useState<ParsedTable | null>(null)
  const [cols, setCols] = useState({ code: 0, name: 1, desc: 2 })
  const [sourceLang, setSourceLang] = useState<string>("nb-NO")
  const [generated, setGenerated] = useState<Generated | null>(null)
  const [seedResults, setSeedResults] = useState<Map<string, SeedResult>>(
    new Map(),
  )

  const [generate, generateState] = useTranslationGenerateTypeValuesMutation()
  errorToast(generateState.error, { title: "Generate failed" })
  const [seed, seedState] = useTranslationSeedTypeValuesMutation()
  errorToast(seedState.error, { title: "Seed failed" })

  const generatedByCode = new Map<string, Generated[number]>()
  for (const value of generated ?? []) generatedByCode.set(value.Code, value)

  const issues = parsed ? findTableIssues(parsed, cols.code) : null
  const seededOk = [...seedResults.values()].filter((r) => r.Ok).length
  const seededFailed = seedResults.size - seededOk

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    file.text().then(setRawText)
  }

  function onParse() {
    const result = parseTable(rawText, hasHeader)
    setParsed(result)
    setGenerated(null)
    if (result) {
      const n = result.header.length
      setCols({ code: 0, name: Math.min(1, n - 1), desc: Math.min(2, n - 1) })
    }
  }

  async function onGenerateTranslations() {
    if (!parsed) return
    const values = parsed.rows
      .map((row) => ({
        Code: (row[cols.code] ?? "").trim(),
        Name: (row[cols.name] ?? "").trim(),
        Description: (row[cols.desc] ?? "").trim() || null,
      }))
      .filter((value) => value.Code !== "" && value.Name !== "")
    if (values.length === 0) return
    const res = await generate({
      variables: {
        input: { TypeSet: typeSet, SourceLanguage: sourceLang, Values: values },
      },
    })
    if (res.data?.generateTypeValues) {
      setGenerated(res.data.generateTypeValues)
      setSeedResults(new Map())
    }
  }

  // Let the user tweak the AI text before seeding.
  function editGenerated(
    code: string,
    languageCode: string,
    field: "Name" | "Description",
    text: string,
  ) {
    setGenerated(
      (prev) =>
        prev?.map((value) =>
          value.Code !== code
            ? value
            : {
                ...value,
                Languages: value.Languages.map((lang) =>
                  lang.LanguageCode !== languageCode
                    ? lang
                    : { ...lang, [field]: text || null },
                ),
              },
        ) ?? null,
    )
  }

  async function onSeed() {
    if (!generated || typeSet.trim() === "") return
    const res = await seed({
      variables: {
        input: {
          TypeSet: typeSet,
          Values: generated.map((value) => ({
            Code: value.Code,
            Languages: value.Languages.filter(
              (lang) => (lang.Name ?? "").trim() !== "",
            ).map((lang) => ({
              LanguageCode: lang.LanguageCode,
              Name: lang.Name ?? "",
              Description: lang.Description ?? null,
            })),
          })),
        },
      },
    })
    if (res.data?.seedTypeValues) {
      const results = res.data.seedTypeValues
      setSeedResults(new Map(results.map((result) => [result.Code, result])))
      // Only on full success — any failed row keeps the operator here, where
      // the per-row errors show.
      if (results.length > 0 && results.every((result) => result.Ok)) {
        onSeeded(typeSet.trim())
      }
    }
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-3">
      <header className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">Add type values</h2>
        <div className="max-w-3xl text-xs text-muted-foreground">
          <p>
            The Translation tab manages two kinds of text.{" "}
            <strong className="text-foreground">App UI texts</strong> (
            <code>common.Features</code>) label the interface itself — buttons,
            panels, columns. They enter the system automatically from the code
            and are handled in the other views.{" "}
            <strong className="text-foreground">Type values</strong> (
            <code>common.TypeValues</code>) label <em>data</em>: the possible
            values a database column can hold — ownership types, building
            statuses, area sources. They never exist in code, so this is where
            they are added: paste the values, let the AI write every language,
            review, and save. Both kinds store their per-language text in{" "}
            <code>common.Translations</code>.
          </p>
        </div>
      </header>

      <TranslationAddObjectsStep number={1} title="Name the type set">
        <p className="translation-addobjects-step-help">
          A type set groups the values of one database column. Its name is that
          column's identity as <code>schema.Table_Column</code> — for example{" "}
          <code>property.CadastreOwnership_InterestTypes</code> for the{" "}
          <code>InterestTypes</code> column of{" "}
          <code>property.CadastreOwnership</code>. Data panels look labels up
          under exactly this key, so it must match what the consuming table
          expects. Seeding into an existing set updates and extends it — nothing
          is deleted.
        </p>
        <div className="flex max-w-3xl flex-wrap gap-3">
          <label className="flex flex-1 basis-72 flex-col gap-1">
            <span className="text-sm font-medium">Type set</span>
            <Input
              value={typeSet}
              placeholder="schema.Table_Column, e.g. property.CadastreOwnership_InterestTypes"
              onChange={(e) => setTypeSet(e.target.value)}
              data-feature-code="UI_BackofficeTranslation_AddObjects_TypeSet"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Values are written in</span>
            <select
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              title="The language of the pasted names/descriptions (Geonorge catalogues are Norwegian). The AI translates from this language into every supported one."
              className="h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm"
            >
              {ADD_OBJECTS_SOURCE_LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        </div>
      </TranslationAddObjectsStep>

      <TranslationAddObjectsStep number={2} title="Provide the values">
        <p className="translation-addobjects-step-help">
          One row per value: <strong className="text-foreground">code</strong>{" "}
          (the stable number or short string stored in the database column),{" "}
          <strong className="text-foreground">name</strong> in the source
          language, and an optional{" "}
          <strong className="text-foreground">description</strong>. Comma-,
          semicolon- and tab-separated all work — the delimiter is
          auto-detected. Example:
        </p>
        <div className="flex max-w-3xl items-start gap-3">
          <pre className="flex-1 overflow-x-auto rounded-md border border-border/60 bg-muted/30 p-2 font-mono text-xs text-muted-foreground">
            {EXAMPLE_ROWS}
          </pre>
          <Button
            size="tiny"
            variant="secondary"
            data-feature-code="UI_BackofficeTranslation_AddObjects_Example"
            onClick={() => setRawText(EXAMPLE_ROWS)}
            title="Fill the paste area with these example rows to try the flow. Nothing is written to the database until step 5."
          >
            Try it
          </Button>
        </div>
        <textarea
          value={rawText}
          rows={10}
          placeholder="Paste rows here — or upload a file below"
          onChange={(e) => setRawText(e.target.value)}
          data-feature-code="UI_BackofficeTranslation_AddObjects_Paste"
          className="min-h-[10rem] max-w-3xl resize-y rounded-md border border-input bg-transparent p-2 font-mono text-xs shadow-sm placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>or upload a file:</span>
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              onChange={onFile}
              data-feature-code="UI_BackofficeTranslation_AddObjects_Upload"
              className="text-xs"
            />
            {fileName ? <span>loaded {fileName}</span> : null}
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={hasHeader}
              onChange={(e) => setHasHeader(e.target.checked)}
            />
            First row is a header
          </label>
          <Button
            size="sm"
            data-feature-code="UI_BackofficeTranslation_AddObjects_Parse"
            onClick={onParse}
            disabled={rawText.trim() === ""}
            title="Parse the table and check it for problems (nothing is written)."
          >
            Parse &amp; verify
          </Button>
        </div>
      </TranslationAddObjectsStep>

      <TranslationAddObjectsStep
        number={3}
        title="Check the parse"
        enabled={!!parsed}
      >
        {parsed ? (
          <>
            <div className="max-w-3xl rounded-md border border-border p-2 text-xs">
              <p>
                Parsed <strong>{parsed.rows.length}</strong> value(s),{" "}
                <strong>{parsed.header.length}</strong> column(s), delimiter:{" "}
                <strong>{parsed.delimiterLabel}</strong>.
              </p>
              {issues &&
              (issues.ragged || issues.emptyCode || issues.dupes.length) ? (
                <ul className="mt-1 list-inside list-disc text-destructive">
                  {issues.ragged ? (
                    <li>
                      {issues.ragged} row(s) have a different column count than
                      the header.
                    </li>
                  ) : null}
                  {issues.emptyCode ? (
                    <li>{issues.emptyCode} row(s) have an empty code cell.</li>
                  ) : null}
                  {issues.dupes.length ? (
                    <li>duplicate code(s): {issues.dupes.join(", ")}</li>
                  ) : null}
                </ul>
              ) : (
                <p className="mt-1 text-primary">No problems found.</p>
              )}
            </div>
            <p className="translation-addobjects-step-help">
              Point each field at the right column if the automatic guess got it
              wrong — the rows below update live.
            </p>
            <div className="flex flex-wrap gap-3">
              {(["code", "name", "desc"] as const).map((field) => (
                <label key={field} className="flex flex-col gap-1 text-xs">
                  <span className="font-medium capitalize">
                    {field === "desc" ? "description" : field} column
                  </span>
                  <select
                    value={cols[field]}
                    onChange={(e) =>
                      setCols((prev) => ({
                        ...prev,
                        [field]: Number(e.target.value),
                      }))
                    }
                    className="h-8 rounded-md border border-input bg-transparent px-2 shadow-sm"
                  >
                    {parsed.header.map((header, i) => (
                      <option key={i} value={i}>
                        {header || `Column ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </>
        ) : (
          <p className="translation-addobjects-step-help">
            Appears after <em>Parse &amp; verify</em>.
          </p>
        )}
      </TranslationAddObjectsStep>

      <TranslationAddObjectsStep
        number={4}
        title="Translate and review"
        enabled={!!parsed}
      >
        {parsed ? (
          <>
            <p className="translation-addobjects-step-help">
              The AI writes <em>Name</em> and <em>Description</em> in every
              supported language for each value (one AI call per value). The
              result is an editable preview — fix any text inline before saving.
              Nothing is written to the database in this step.
            </p>
            <div>
              <Button
                size="sm"
                data-feature-code="UI_BackofficeTranslation_AddObjects_Generate"
                onClick={onGenerateTranslations}
                disabled={generateState.loading}
                isLoading={generateState.loading}
              >
                {generateState.loading
                  ? "Generating…"
                  : `Generate translations (AI) · ${parsed.rows.length} value(s)`}
              </Button>
            </div>
            <div className="flex max-w-4xl flex-col gap-2">
              {parsed.rows.map((row, i) => {
                const code = (row[cols.code] ?? "").trim()
                return (
                  <TranslationAddObjectsPreviewRow
                    key={i}
                    code={code}
                    name={row[cols.name] ?? ""}
                    description={row[cols.desc] ?? ""}
                    generated={generatedByCode.get(code)}
                    seedResult={seedResults.get(code)}
                    onEditGenerated={(languageCode, field, text) =>
                      editGenerated(code, languageCode, field, text)
                    }
                  />
                )
              })}
            </div>
          </>
        ) : (
          <p className="translation-addobjects-step-help">
            Appears after <em>Parse &amp; verify</em>.
          </p>
        )}
      </TranslationAddObjectsStep>

      <TranslationAddObjectsStep
        number={5}
        title="Save to database"
        enabled={!!generated}
      >
        <p className="translation-addobjects-step-help">
          Writes the type set, its values and all reviewed translations to{" "}
          <code>common.TypeValues</code> and <code>common.Translations</code>.
          Each value is saved independently, so one bad row never blocks the
          rest — per-value results show on the rows above. The labels reach the
          app on the next <em>Publish</em>.
        </p>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            data-feature-code="UI_BackofficeTranslation_AddObjects_Seed"
            onClick={onSeed}
            disabled={!generated || seedState.loading || typeSet.trim() === ""}
            isLoading={seedState.loading}
            title={
              typeSet.trim() === ""
                ? "Name the type set in step 1 first."
                : "Persist via the api.Upsert_Common_* procs."
            }
          >
            {seedState.loading
              ? "Saving…"
              : `Save to database · ${generated?.length ?? 0} value(s)`}
          </Button>
          {seedResults.size > 0 ? (
            <span className="text-xs">
              <span className="text-primary">{seededOk} saved</span>
              {seededFailed > 0 ? (
                <span className="text-destructive">
                  {" "}
                  · {seededFailed} failed (see rows above)
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      </TranslationAddObjectsStep>
    </div>
  )
}

export default TranslationAddObjectsView
